import React from 'react';
import { Box, Button, Switch, Typography } from '@mui/material';
import { INTEGRATION_SETTINGS_COPY as COPY } from '../utils/integrationSettingsCopy';
import type { ShopifySettingLock } from '../services/integrationApi';

interface ShopifyScopeGatedSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  lock?: ShopifySettingLock;
  onGrant?: () => void;
  granting?: boolean;
  /** Custom-app installs cannot re-run public OAuth from here. */
  customApp?: boolean;
  /** The store's plan does not include this feature. Stays off and shows the notice; never blocks Next or Save. */
  planLockedNotice?: string;
}

/**
 * Settings switch that stays off when Shopify did not grant the matching scope.
 */
const ShopifyScopeGatedSwitch: React.FC<ShopifyScopeGatedSwitchProps> = ({
  checked,
  onChange,
  lock,
  onGrant,
  granting,
  customApp,
  planLockedNotice,
}) => {
  const planLocked = Boolean(planLockedNotice);
  const locked = lock?.locked === true || planLocked;
  // Locked UI always renders off; push that into parent state so Save does not
  // keep posting the stored true and get rejected by the settings API.
  React.useEffect(() => {
    if (locked && checked) onChange(false);
  }, [locked, checked, onChange]);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
      <Switch
        size="small"
        checked={locked ? false : checked}
        disabled={locked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {planLocked && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', maxWidth: 260, lineHeight: 1.4 }}>
          {planLockedNotice}
        </Typography>
      )}
      {lock?.locked === true && (
        <>
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', textAlign: 'right', maxWidth: 220, lineHeight: 1.4 }}>
            {customApp ? COPY.scopeLock.customApp : COPY.scopeLock.notGranted}
          </Typography>
          {!customApp && onGrant && (
            <Button size="small" onClick={onGrant} disabled={granting}>
              {granting ? 'Opening Shopify…' : COPY.scopeLock.grant}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default ShopifyScopeGatedSwitch;
